import React, { useContext, useState, useRef } from "react";
import { TbLayoutSidebar } from "react-icons/tb";
import { IoShareOutline } from "react-icons/io5";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";

import logo from '../../assets/images/logo.png';
import userImage from '../../assets/icons/user-image.png';

import { RiUserLine } from "react-icons/ri";
import { CiServer, CiMoneyCheck1 } from "react-icons/ci";
import { MdLogout } from "react-icons/md";
import { FaXRay } from "react-icons/fa";
import { PiReceiptLight } from "react-icons/pi";
import { BsFiles } from "react-icons/bs";
import { GrUpgrade } from "react-icons/gr";

import './chat-page-header-responsive.css';
import { useResponsiveLayout } from "../../contexts/ResponsiveLayoutContext";
import ProfileAvatar from "@minohealth/common/components/profile-avatar";
import useSwitchAccounts from "@minohealth/common/hooks/useSwitchAccounts";
import { removeCookie, showConfirm } from "@minohealth/common/utils";
import { setUser } from "@minohealth/common/services/DataStoreService/reducers/AppReducer";
import AppNavigationContext from "@minohealth/common/services/AppNavigationContext";
import useOnClickOutside from "@minohealth/common/hooks/useOnClickOutside";
import ThemeToggle from "./extras/ThemeToggle";
import { FeatureFlags } from "@minohealth/common/lib/feature-flags";
import ProfileMenuCustom from "../profile-menu/ProfileMenuCustom";

// --- Main Component ---

export default function ChatPageHeader({
    title = "Untitled Chat",
    isSidebarExpanded,
    onToggleSidebar,
    onCreateChat,
    onShareChat,
    variant = "chat" // "chat" | "app"
}) {
    const dispatch = useDispatch();
    const { appNavHistory } = useContext(AppNavigationContext) || {};
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.app.datastate);
    const isLoggedIn = user?._id != null;
    const { isCompact } = useResponsiveLayout();
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

    // Profile Menu Logic (for "app" variant)
    const { profiles, selectedProfileId, switchAccount } = useSwitchAccounts();
    const selectedProfile = profiles?.find((profile) => profile.id === selectedProfileId);

    function handleLogout() {
        showConfirm(
            t("menu.confirm-logout", "Are you sure you want to log out?"),
            t("menu.logout", "Logout"),
            "critical"
        )
            .then(() => {
                dispatch(setUser({}));
                removeCookie();
                if (appNavHistory) {
                    appNavHistory.replace("/login");
                } else {
                    window.location.href = "/login";
                }
            })
            .catch((err) => {
                Sentry.captureException(err, {
                    extra: {
                        context: 'ChatPageHeader - user logout',
                        hasUser: !!user,
                        userEmail: user?.email
                    }
                });
            });
    }

    return (
        <header
            className="flex items-center h-16 px-4 bg-background relative transition-colors duration-300 border-b border-border"
            data-compact={isCompact}
        >
            {/* Left Section */}
            <div className="flex items-center h-full">
                {variant === "chat" ? (
                    // Chat Mode: Sidebar Toggle
                    isLoggedIn && (
                        <>
                            <button
                                onClick={onToggleSidebar}
                                className={`p-2 mr-3 text-muted-foreground hover:text-foreground bg-transparent rounded-md focus:outline-none ${!isSidebarExpanded ? 'md:hidden' : ''}`}
                                title={isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
                                data-test="sidebar-toggle-btn"
                            >
                                <TbLayoutSidebar className="w-6 h-6" />
                            </button>

                            <div className={`h-6 w-px bg-border mr-4 ${!isSidebarExpanded ? 'md:hidden' : ''}`}></div>
                        </>
                    )
                ) : (
                    // App Mode: Logo
                    <Link to="/" className="flex items-center">
                        <div className="flex items-center gap-2">
                            <img src={logo} alt="minoHealth AI Labs" className="h-7 object-contain" />
                            <span className="font-semibold text-sm tracking-tight" style={{ fontFamily: 'MuseoSans, sans-serif', color: 'var(--foreground)' }}>MinoHealth AI Labs</span>
                        </div>
                    </Link>
                )}
            </div>

            {/* Center Section */}
            <div className="flex-1 flex justify-center items-center overflow-hidden min-w-0">
                {variant === "chat" && (
                    <div className="flex items-center gap-3 truncate px-2 max-w-full min-w-0">
                        <h1 className="text-lg font-semibold text-foreground truncate min-w-0">
                            {title}
                        </h1>
                    </div>
                )}
            </div>

            {/* Right Section */}
            <div className="flex items-center justify-end min-w-[4rem]">
                {variant === "chat" ? (
                    // Chat Mode: Share Button or Login/Signup
                    isLoggedIn ? (
                        <button
                            onClick={onShareChat}
                            className="p-2 text-muted-foreground hover:text-foreground bg-transparent rounded-md focus:outline-none transition-colors"
                            title="Share chat"
                        >
                            <IoShareOutline className="w-5 h-5" />
                        </button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Link
                                to="/login"
                                className="px-4 py-1.5 text-sm font-medium text-foreground bg-transparent hover:bg-muted rounded-full transition-colors border border-border"
                            >
                                Login
                            </Link>
                            <Link
                                to="/register"
                                className="hidden md:flex px-4 py-1.5 text-sm font-medium text-gray-700 rounded-full transition-colors"
                                style={{
                                    background: "#01d388"
                                }}
                            >
                                Sign up
                            </Link>
                        </div>
                    )
                ) : (
                    // App Mode: Profile Menu (Custom)
                    <div className="flex items-center relative">
                        <ProfileMenuCustom
                            isOpen={isProfileMenuOpen}
                            onClose={() => setIsProfileMenuOpen(false)}
                            user={user}
                            profiles={profiles}
                            selectedProfile={selectedProfile}
                            switchAccount={switchAccount}
                            handleLogout={handleLogout}
                            containerClassName="absolute top-full right-0 mt-2 z-50"
                        />

                        <button
                            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                            className="flex items-center gap-2 px-2 h-10 rounded-lg bg-transparent hover:bg-muted transition-colors group focus:outline-none"
                        >
                            <div className="relative">
                                <ProfileAvatar name={selectedProfile?.name ?? selectedProfile?.email ?? (user?.firstName ? `${user.firstName} ${user.lastName ?? ""}`.trim() : (user?.email ?? "?"))} size={32} className="rounded-full" />
                            </div>
                            <span className="font-medium text-[14px] text-foreground hidden md:block">
                                {selectedProfile?.name?.split(" ")[0] ?? selectedProfile?.email ?? user?.email ?? "My Profile"}
                            </span>
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
}
